import { useQuery } from "@tanstack/react-query";
import {
  GET_CMS_AWARDS,
  GET_CMS_CAREER_CLIMB,
  GET_CMS_CAREER_CLIMBS,
  GET_CMS_CAREER_PLAN_NEXT_STEPS,
  GET_CMS_COURSE,
  GET_CMS_COURSES,
  GET_CMS_FACET_TYPES,
  GET_CMS_FACETS,
  GET_CMS_HAT_CONTENT,
  GET_CMS_HAT_CONTENT_MS,
  GET_CMS_HAT_INDEX,
  GET_CMS_LADDER_CONTENT,
  GET_CMS_LADDERS_CONTENT,
  GET_CMS_REPORTS,
  GET_CMS_TOPICS,
  GET_CMS_REGIONS,
  GET_CMS_PATHWAYS,
  GET_CMS_ACTIVITY,
  GET_CMS_BUILDING_BLOCKS,
  GET_CMS_BUILDING_BLOCK_ACTIVITIES,
  GET_CMS_SHOUT_OUT_CONNECTORS,
  GET_PATHWAY_CONTENT,
  GET_CMS_VIDEO,
  GET_MS_CMS_HAT_INDEX,
  GET_CMS_HAT_CONTENT_MS_V2,
  GET_CMS_MS_HAT_DISPLAY,
  GET_HAT_CARD_IMAGES,
} from "../api/queries";
import { useMemo } from "react";
import { useProductFeatures } from "./useProductFeatures";
import { getFeatureFlag } from "./useFeatureFlag";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { graphQLClient } from "../graphqlClient";
import type {
  CMSClimberFacetType,
  CMSClimberFacet,
  MSHatIndex,
  CMSHatIndex,
  CMSHatRegion,
  CMSTopic,
  CMSPathway,
  CMSCareerCluster,
  CMSRegion,
  CMSLadderFull,
  CMSActivityFull,
  CMSCareerClimb,
  CMSCourse,
  CMSReport,
  CMSAward,
  CMSBuildingBlockActivity,
  CMSBuildingBlock,
  CMSShoutOutConnector,
  CMSUploadedVideo,
  CMSCareerPlanNextStep,
  CMSAssessment,
  CMSGetFacetTypesResult,
  CMSGetFacetsResult,
  CMSGetMSHatIndexResult,
  CMSGetHatIndexResult,
  CMSGetHatContentResult,
  CMSGetTopicsResult,
  CMSGetPathwayResult,
  CMSGetPathwaysResult,
  CMSGetRegionsResult,
  CMSGetLaddersResult,
  CMSGetLadderResult,
  CMSGetActivityResult,
  CMSGetCareerClimbResult,
  CMSGetCourseResult,
  CMSGetReportsResult,
  CMSGetCareerClimbsResult,
  CMSGetCoursesResult,
  CMSGetAwardsResult,
  CMSGetBuildingBlockActivitiesResult,
  CMSGetBuildingBlocksResult,
  CMSGetVideoResult,
  CMSGetCareerPlanNextStepsResult,
  CMSMSHatDisplay,
  CMSGetMSHatDisplayResult,
} from "../types/cms-types";

const devCacheKey = Math.round(Date.now() / 1000);

interface CMSCacheOptions {
  cacheKey: number;
}

interface CMSDraftOptions {
  drafts: boolean;
}

interface CMSOptions extends CMSDraftOptions, CMSCacheOptions {
  retry: number;
}

interface RQCacheOptions {
  staleTime: number;
  cacheTime: number;
  gcTime: number;
}

const getCMSCacheOptions = (): CMSCacheOptions => {
  const isCacheEnabled = getFeatureFlag("CMS_CACHE", true);
  return { cacheKey: isCacheEnabled ? 0 : devCacheKey };
};

// Cache options for react-query
const getRQCacheOptions = (): RQCacheOptions => {
  const isCacheEnabled = getFeatureFlag("CMS_CACHE", true);

  if (isCacheEnabled) {
    // Normal user mode
    return {
      staleTime: 1000 * 60 * 10, // Consider data fresh for 10 minutes (same as production appsync cache duration)
      cacheTime: 1000 * 60 * 60 * 12, // Keep unused data in cache for 12 hours
      gcTime: 1000 * 60 * 60 * 12, // Keep unused data in cache for 12 hours
    };
  }

  // Developer mode
  return {
    staleTime: 0, // For local dev, don't cache
    cacheTime: 1000 * 60 * 60 * 4, // Keep unused data in cache for 4 hours
    gcTime: 1000 * 60 * 60 * 4, // Keep unused data in cache for 4 hours
  };
};

const getCMSDraftOptions = (): CMSDraftOptions => {
  return { drafts: getFeatureFlag("CMS_DRAFTS", false) };
};

const getCMSOptions = (): CMSOptions => ({
  ...getCMSDraftOptions(),
  ...getCMSCacheOptions(),
  retry: 4,
});

const cmsOptions = getCMSOptions();
const cmsOptionsCacheKey = JSON.stringify(cmsOptions);

interface UseFacetTypesResult {
  facetTypes: CMSClimberFacetType[];
  loading: boolean;
  error: Error | null;
}

export const useFacetTypes = (): UseFacetTypesResult => {
  const { data, isLoading, error } = useQuery<CMSGetFacetTypesResult>({
    queryKey: ["cmsFacetTypes", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_FACET_TYPES, {
        variables: getCMSOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter(
    "Could not load the list of Facet Types",
    error,
    false,
  );

  return {
    facetTypes: data?.cmsClimberFacetTypes || [],
    loading: isLoading,
    error,
  };
};

interface UseFacetsResult {
  facets: CMSClimberFacet[];
  loading: boolean;
  error: Error | null;
}

export const useFacets = (): UseFacetsResult => {
  const { data, isLoading, error } = useQuery<CMSGetFacetsResult>({
    queryKey: ["cmsFacets", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_FACETS, { variables: getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of Facets", error, false);

  return { facets: data?.cmsClimberFacets || [], loading: isLoading, error };
};

interface UseHatIndexResult {
  hatIndex: MSHatIndex[];
  loading: boolean;
  error: Error | null;
}

export const useMSHatIndex = (): UseHatIndexResult => {
  const { data, isLoading, error } = useQuery<CMSGetMSHatIndexResult>({
    queryKey: ["cmsMSHatIndex", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_MS_CMS_HAT_INDEX, {
        variables: getCMSOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of Hats", error);

  const { matchesProductPlans } = useProductFeatures();
  const hatIndex = data?.cmsHatsIndex || [];
  const filteredHatIndex = useMemo(
    () => hatIndex.filter((hat) => matchesProductPlans(hat.productPlans)),
    [hatIndex, matchesProductPlans],
  );

  return { hatIndex: filteredHatIndex, loading: isLoading, error };
};

interface UseFullHatIndexResult {
  hatIndex: CMSHatIndex[];
  allHats: CMSHatIndex[];
  loading: boolean;
  error: Error | null;
}

export const useHatIndex = (): UseFullHatIndexResult => {
  const { data, isLoading, error } = useQuery<CMSGetHatIndexResult>({
    queryKey: ["cmsHatIndex", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_HAT_INDEX, { variables: getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of Hats", error);

  const { matchesProductPlans } = useProductFeatures();
  const hatIndex = data?.cmsHatsIndex || [];
  const filteredHatIndex = useMemo(
    () => hatIndex.filter((hat) => matchesProductPlans(hat.productPlans)),
    [hatIndex, matchesProductPlans],
  );

  return {
    hatIndex: filteredHatIndex,
    allHats: data?.cmsHatsIndex || [],
    loading: isLoading,
    error,
  };
};

interface UseHatRegionsContentResult {
  hatRegions: CMSHatRegion[];
  loading: boolean;
  error: Error | null;
}

export const useHatRegionsContent = (
  hatId: string,
  stateId?: number,
  msaId?: number,
): UseHatRegionsContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetHatContentResult>({
    queryKey: ["cmsHatContent", hatId, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_HAT_CONTENT, {
        hatId,
        msaId: msaId ?? -1,
        stateId: stateId ?? 36,
        ...getCMSOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Hat regional data", error);

  return { hatRegions: data?.cmsHatsRegions || [], loading: isLoading, error };
};

export const useHatRegionsMSContent = (
  hatId: string | undefined,
  stateId?: number,
  msaId?: number,
): UseHatRegionsContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetHatContentResult>({
    queryKey: ["cmsHatContentMS", hatId, stateId, msaId, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_HAT_CONTENT_MS, {
        hatId,
        msaId: msaId ?? -1,
        stateId: stateId ?? 36,
        ...getCMSOptions(),
      }),
    enabled: !!hatId,
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Hat regional data", error);

  return { hatRegions: data?.cmsHatsRegions || [], loading: isLoading, error };
};

export const useHatRegionsMSContentV2 = (
  hatId: string | undefined,
  stateId?: number,
  msaId?: number,
): UseHatRegionsContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetHatContentResult>({
    queryKey: ["cmsHatContentMSV2", hatId, stateId, msaId, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_HAT_CONTENT_MS_V2, {
        hatId,
        msaId: msaId ?? -1,
        stateId: stateId ?? 36,
        ...getCMSOptions(),
      }),
    enabled: !!hatId,
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Hat regional data", error);

  return { hatRegions: data?.cmsHatsRegions || [], loading: isLoading, error };
};

// Consolidated MS hat display query — returns hat + salary + district programs in one call
export interface UseMSHatDisplayResult {
  hatDisplay: CMSMSHatDisplay | null;
  loading: boolean;
  error: Error | null;
}

export const useMSHatDisplay = (
  hatId: string | undefined,
  stateId?: string | number,
  msaId?: string | number,
  districtId?: string,
  middleSchoolId?: string,
): UseMSHatDisplayResult => {
  const { data, isLoading, error } = useQuery<CMSGetMSHatDisplayResult>({
    queryKey: [
      "cmsMSHatDisplay",
      hatId,
      stateId,
      msaId,
      districtId,
      middleSchoolId,
      cmsOptionsCacheKey,
    ],
    queryFn: () =>
      graphQLClient.request(GET_CMS_MS_HAT_DISPLAY, {
        hatId,
        stateId: stateId ? String(stateId) : undefined,
        msaId: msaId ? String(msaId) : undefined,
        districtId: districtId || undefined,
        middleSchoolId: middleSchoolId || undefined,
        ...getCMSOptions(),
      }),
    enabled: !!hatId,
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Hat display data", error);

  return {
    hatDisplay: data?.cmsMSHatDisplay || null,
    loading: isLoading,
    error,
  };
};

export interface UseHatCardImagesResult {
  map: Map<
    string,
    { alt?: string; smallSquare?: string; mediumSquare?: string }
  >;
  isLoading: boolean;
}

export const useHatCardImages = (hatIds: string[]): UseHatCardImagesResult => {
  const sortedIds = useMemo(() => [...hatIds].sort(), [hatIds]);
  const { data, isLoading, isFetching } = useQuery<{
    cmsHatCardImages: {
      id: string;
      hatCardImage?: {
        alt?: string;
        smallSquare?: string;
        mediumSquare?: string;
      };
    }[];
  }>({
    queryKey: ["hatCardImages", sortedIds, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_HAT_CARD_IMAGES, {
        hatIds: sortedIds,
        ...getCMSOptions(),
      }),
    enabled: sortedIds.length > 0,
    ...getRQCacheOptions(),
  });
  const map = useMemo(() => {
    const next = new Map<
      string,
      { alt?: string; smallSquare?: string; mediumSquare?: string }
    >();
    for (const h of data?.cmsHatCardImages || []) {
      if (h.hatCardImage) next.set(h.id, h.hatCardImage);
    }
    return next;
  }, [data]);
  return {
    map,
    // Treat as loading whenever the query is in flight, including refetches
    // for a different set of related hat IDs.
    isLoading: sortedIds.length > 0 && (isLoading || isFetching),
  };
};

interface UseTopicsResult {
  topics: string[];
  subTopics: string[];
  cmsTopics: CMSTopic[];
  loading: boolean;
  error: Error | null;
}

export const useTopics = (): UseTopicsResult => {
  const { data, isLoading, error } = useQuery<CMSGetTopicsResult>({
    queryKey: ["cmsPathways", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_TOPICS, { variables: getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of topics", error);

  const cmsTopics = data?.cmsTopics || [];
  const topics = cmsTopics
    .filter((topic) => !topic.isSubTopic)
    .map((t) => t.name);
  const subTopics = cmsTopics
    .filter((topic) => topic.isSubTopic)
    .map((t) => t.name);

  return { topics, subTopics, cmsTopics, loading: isLoading, error };
};

interface UsePathwayContentResult {
  pathway: CMSPathway | null | undefined;
  loading: boolean;
  error: Error | null;
}

export const usePathwayContent = (
  pathwayId: string,
): UsePathwayContentResult => {
  const { data, error, isLoading } = useQuery<CMSGetPathwayResult>({
    queryKey: ["pathwayContent", pathwayId],
    queryFn: () =>
      graphQLClient.request(GET_PATHWAY_CONTENT, { id: pathwayId }),
    enabled: !!pathwayId,
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load pathway content", error);

  return {
    pathway: data?.cmsPathway,
    loading: isLoading,
    error,
  };
};

interface ClusterWithPathways {
  cluster: CMSCareerCluster;
  pathways: CMSPathway[];
}

interface UsePathwaysResult {
  clusters: ClusterWithPathways[];
  cmsPathways: CMSPathway[];
  loading: boolean;
  error: Error | null;
}

export const usePathways = (): UsePathwaysResult => {
  const { matchesProductPlans } = useProductFeatures();
  const { data, isLoading, error } = useQuery<CMSGetPathwaysResult>({
    queryKey: ["cmsPathways", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_PATHWAYS, { variables: getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of pathways", error);

  const clusters = useMemo(() => {
    const cmsPathways = data?.cmsPathways || [];

    const clusterMap = new Map<string, ClusterWithPathways>();

    cmsPathways
      .filter((pathway) =>
        matchesProductPlans(pathway.productPlan?.map((p) => p.code) || []),
      )
      .forEach((pathway) => {
        pathway.careerCluster.forEach((cluster) => {
          if (!clusterMap.has(cluster.id)) {
            clusterMap.set(cluster.id, {
              cluster,
              pathways: [],
            });
          }
          clusterMap.get(cluster.id)!.pathways.push(pathway);
        });
      });

    return Array.from(clusterMap.values());
  }, [data?.cmsPathways]);

  return {
    clusters,
    cmsPathways: data?.cmsPathways || [],
    loading: isLoading,
    error,
  };
};

interface UseRegionsResult {
  countries: CMSRegion[];
  states: CMSRegion[];
  msas: CMSRegion[];
  loading: boolean;
  error: Error | null;
}

export const useRegions = (): UseRegionsResult => {
  const { data, isLoading, error } = useQuery<CMSGetRegionsResult>({
    queryKey: ["cmsRegions", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_REGIONS, { variables: getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of regions", error);

  const cmsRegions = data?.cmsRegions || [];
  const countries = cmsRegions.filter((topic) => topic.type === "country");
  const states = cmsRegions.filter((topic) => topic.type === "state");
  const msas = cmsRegions.filter((topic) => topic.type === "msa");

  return { countries, states, msas, loading: isLoading, error };
};

interface TopicFilter {
  name: string;
}

interface LaddersContentParams {
  topicFilter?: TopicFilter[];
  subTopicFilter?: TopicFilter[];
}

interface UseLaddersContentResult {
  ladderContent: CMSLadderFull[];
  allLadders: CMSLadderFull[];
  loading: boolean;
  error: Error | null;
}

export const useLaddersContent = (
  params: LaddersContentParams = {},
): UseLaddersContentResult => {
  const { topicFilter = [], subTopicFilter = [] } = params;

  const { data, isLoading, error } = useQuery<CMSGetLaddersResult>({
    queryKey: ["cmsLaddersContent", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_LADDERS_CONTENT, {
        variables: getCMSOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Error loading ladders content", error);

  const { matchesProductPlans } = useProductFeatures();
  const cmsLadders = data?.cmsLadders || [];
  const hasFilters = topicFilter.length > 0 || subTopicFilter.length > 0;

  const ladderContent = useMemo(() => {
    const planLadders = cmsLadders.filter((ladder) => {
      const ladderPlans =
        ladder.assessments
          ?.flatMap((a) => a.productPlan)
          .map((plan) => plan?.code) || [];
      return matchesProductPlans(ladderPlans);
    });

    return hasFilters
      ? planLadders.filter((ladder) => {
          const topics = (
            ladder.topics?.filter((t) => !t.isSubTopic) || []
          ).map((t) => t.name);
          const subTopics = (
            ladder.topics?.filter((t) => t.isSubTopic) || []
          ).map((t) => t.name);
          return (
            topicFilter
              .map((tf) => tf.name)
              .some((tf) => topics.includes(tf)) ||
            subTopicFilter
              .map((tf) => tf.name)
              .some((stf) => subTopics.includes(stf))
          );
        })
      : planLadders;
  }, [cmsLadders, topicFilter, subTopicFilter, matchesProductPlans]);

  return { ladderContent, allLadders: cmsLadders, loading: isLoading, error };
};

interface UseLadderContentResult {
  ladder: CMSLadderFull | null | undefined;
  loading: boolean;
  error: Error | null;
}

export const useLadderContent = (id: string): UseLadderContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetLadderResult>({
    queryKey: ["cmsLadderContent", id, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_LADDER_CONTENT, { id, ...getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Ladder content", error);

  return { ladder: data?.cmsLadder, loading: isLoading, error };
};

interface UseActivityContentResult {
  activity: CMSActivityFull | null | undefined;
  loading: boolean;
  error: Error | null;
}

export const useActivityContent = (
  id: string | null,
): UseActivityContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetActivityResult>({
    queryKey: ["cmsActivityContent", id, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_ACTIVITY, { id, ...getCMSOptions() }),
    enabled: !!id,
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Activity content", error);

  return { activity: data?.cmsActivity, loading: isLoading, error };
};

interface UseCareerClimbContentResult {
  careerClimb: CMSCareerClimb | null | undefined;
  loading: boolean;
  error: Error | null;
}

export const useCareerClimbContent = (
  id: string,
): UseCareerClimbContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetCareerClimbResult>({
    queryKey: ["cmsCareerClimbContent", id, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_CAREER_CLIMB, {
        id,
        ...getCMSDraftOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Career Climb content", error);

  return { careerClimb: data?.cmsCareerClimb, loading: isLoading, error };
};

interface UseCourseContentResult {
  course: CMSCourse | null | undefined;
  loading: boolean;
  error: Error | null;
}

export const useCourseContent = (id: string): UseCourseContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetCourseResult>({
    queryKey: ["cmsCourseContent", id, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_COURSE, {
        id,
        ...getCMSDraftOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Career Climb content", error);

  return { course: data?.cmsCourse, loading: isLoading, error };
};

interface UseCMSReportsResult {
  reports: CMSReport[];
  loading: boolean;
  error: Error | null;
}

export const useCMSReports = (): UseCMSReportsResult => {
  const { matchesProductPlans, isLoading: isProductFeaturesLoading } =
    useProductFeatures();

  const { data, isLoading, error } = useQuery<CMSGetReportsResult>({
    queryKey: ["cmsReports", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_REPORTS, { variables: getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  const filteredReports = useMemo(() => {
    return (
      data?.cmsReports.filter((report) =>
        matchesProductPlans(report.productPlan?.map((p) => p.code) || []),
      ) || []
    );
  }, [data?.cmsReports, matchesProductPlans, isProductFeaturesLoading]);

  useReactQueryErrorReporter("Could not load the list of Reports", error);

  return {
    reports: filteredReports,
    loading: isLoading || isProductFeaturesLoading,
    error,
  };
};

interface UseCareerClimbsContentResult {
  careerClimbs: CMSCareerClimb[];
  allCareerClimbs: CMSCareerClimb[];
  loading: boolean;
  error: Error | null;
}

export const useCareerClimbsContent = (): UseCareerClimbsContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetCareerClimbsResult>({
    queryKey: ["cmsCareerClimbs", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_CAREER_CLIMBS, {
        variables: getCMSOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of Career Climbs", error);

  const { matchesProductPlans } = useProductFeatures();
  const filteredCareerClimb = useMemo(() => {
    return (
      data?.cmsCareerClimbs.filter((careerClimb) =>
        matchesProductPlans(careerClimb.productPlan?.map((p) => p.code) || []),
      ) || []
    );
  }, [data?.cmsCareerClimbs, matchesProductPlans]);

  return {
    careerClimbs: filteredCareerClimb,
    allCareerClimbs: data?.cmsCareerClimbs || [],
    loading: isLoading,
    error,
  };
};

interface UseCoursesContentResult {
  courses: CMSCourse[];
  allCourses: CMSCourse[];
  loading: boolean;
  error: Error | null;
}

export const useCoursesContent = (): UseCoursesContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetCoursesResult>({
    queryKey: ["cmsCourses", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_COURSES, {
        variables: getCMSOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of courses", error);

  const { matchesProductPlans } = useProductFeatures();
  const filteredCourses = useMemo(() => {
    return (
      data?.cmsCourses.filter((course) =>
        matchesProductPlans(course.productPlan?.map((p) => p.code) || []),
      ) || []
    );
  }, [data?.cmsCourses, matchesProductPlans]);

  return {
    courses: filteredCourses,
    allCourses: data?.cmsCourses || [],
    loading: isLoading,
    error,
  };
};

export const getAssessmentForLadder = (
  cmsLadder: CMSLadderFull | null | undefined,
  previousActivityReferenceId?: string,
): CMSAssessment | undefined => {
  if (previousActivityReferenceId) {
    const assessment = cmsLadder?.assessments?.find(
      (a) => a.assessment === previousActivityReferenceId,
    );
    if (assessment) {
      if (!assessment.active) {
        console.warn(
          `Assessment ${assessment.assessment} is not active but this climber previously started it, so using that one.`,
        );
      }
      return assessment;
    }
    console.error(
      `Climber previously started assessment ${previousActivityReferenceId} but it is not in the ladder ${cmsLadder?.id}`,
    );
  }

  const activeAssessment = cmsLadder?.assessments?.find((a) => a.active);
  if (!activeAssessment) {
    return cmsLadder?.assessments?.[0];
  }
  return activeAssessment;
};

interface UseAwardsResult {
  awards: CMSAward[];
  allAwards: CMSAward[];
  loading: boolean;
  error: Error | null;
}

export const useAwards = (earnedAwardIds?: string[]): UseAwardsResult => {
  const { matchesProductPlans } = useProductFeatures();

  const { data, isLoading, error } = useQuery<CMSGetAwardsResult>({
    queryKey: ["cmsAwards", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_AWARDS, { variables: getCMSOptions() }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the list of Awards", error, false);

  const earnedSet = useMemo(
    () => new Set(earnedAwardIds ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [earnedAwardIds?.join(",")],
  );

  const filteredAwards = useMemo(() => {
    return (
      data?.cmsAwards.filter(
        (award) =>
          matchesProductPlans(award.productPlan?.map((p) => p.code) || []) ||
          earnedSet.has(String(award.id)),
      ) || []
    );
  }, [data?.cmsAwards, matchesProductPlans, earnedSet]);

  return {
    awards: filteredAwards,
    allAwards: data?.cmsAwards || [],
    loading: isLoading,
    error,
  };
};

interface UseBuildingBlockActivitiesResult {
  buildingBlockActivities: CMSBuildingBlockActivity[];
  loading: boolean;
  error: Error | null;
}

export const useBuildingBlockActivities =
  (): UseBuildingBlockActivitiesResult => {
    const { data, isLoading, error } =
      useQuery<CMSGetBuildingBlockActivitiesResult>({
        queryKey: ["cmsBuildingBlockActivities", cmsOptionsCacheKey],
        queryFn: () =>
          graphQLClient.request(GET_CMS_BUILDING_BLOCK_ACTIVITIES, {
            variables: getCMSOptions(),
          }),
        ...getRQCacheOptions(),
      });

    useReactQueryErrorReporter(
      "Could not load the list of Building Block Activities",
      error,
      false,
    );

    return {
      buildingBlockActivities: data?.cmsBuildingBlockActivities || [],
      loading: isLoading,
      error,
    };
  };

interface UseBuildingBlocksResult {
  buildingBlocks: CMSBuildingBlock[];
  loading: boolean;
  error: Error | null;
}

export const useBuildingBlocks = (): UseBuildingBlocksResult => {
  const { data, isLoading, error } = useQuery<CMSGetBuildingBlocksResult>({
    queryKey: ["cmsBuildingBlocks", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_BUILDING_BLOCKS, {
        variables: getCMSOptions(),
      }),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter(
    "Could not load the list of Building Blocks",
    error,
    false,
  );

  return {
    buildingBlocks: data?.cmsBuildingBlocks || [],
    loading: isLoading,
    error,
  };
};

interface UseShoutOutConnectorsResult {
  connectors: CMSShoutOutConnector[];
  loading: boolean;
  error: Error | null;
}

interface CMSGetShoutOutConnectorsResult {
  cmsBuildingBlocks: CMSShoutOutConnector[];
}

export const useShoutOutConnectors = (): UseShoutOutConnectorsResult => {
  const { data, isLoading, error } = useQuery<CMSGetShoutOutConnectorsResult>({
    queryKey: ["cmsShoutOutConnectors", cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_SHOUT_OUT_CONNECTORS, getCMSOptions()),
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter(
    "Could not load the list of Shout-Out Connectors",
    error,
    false,
  );

  return {
    connectors: data?.cmsBuildingBlocks || [],
    loading: isLoading,
    error,
  };
};

interface UseVideoContentResult {
  video: CMSUploadedVideo | null | undefined;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch CMS video data by ID
 */
export const useVideoContent = (id: string): UseVideoContentResult => {
  const { data, isLoading, error } = useQuery<CMSGetVideoResult>({
    queryKey: ["cmsVideoContent", id, cmsOptionsCacheKey],
    queryFn: () =>
      graphQLClient.request(GET_CMS_VIDEO, { id, ...getCMSOptions() }),
    enabled: !!id,
    ...getRQCacheOptions(),
  });

  useReactQueryErrorReporter("Could not load the Video content", error);

  return { video: data?.cmsVideo, loading: isLoading, error };
};

interface UseCareerPlanNextStepsResult {
  careerPlanNextSteps: CMSCareerPlanNextStep[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch CMS career plan next steps data
 */
export const useCareerPlanNextSteps = (): UseCareerPlanNextStepsResult => {
  const { data, isLoading, error } = useQuery<CMSGetCareerPlanNextStepsResult>({
    queryKey: ["cmsCareerPlanNextSteps", cmsOptionsCacheKey],
    queryFn: async () => {
      try {
        return await graphQLClient.request(GET_CMS_CAREER_PLAN_NEXT_STEPS, {
          variables: getCMSOptions(),
        });
      } catch (err: unknown) {
        // Handle GraphQL errors where null is returned for non-nullable type
        // This can happen when the backend doesn't have next steps data yet
        const graphQLErr = err as {
          response?: { errors?: { message?: string }[] };
        };
        const isNullError = graphQLErr?.response?.errors?.some((e) =>
          e?.message?.includes("Cannot return null for non-nullable type"),
        );
        if (isNullError) {
          console.warn(
            "GraphQL returned null for cmsCareerPlanNextSteps, using empty array",
          );
          return { cmsCareerPlanNextSteps: [] };
        }
        throw err;
      }
    },
    ...getRQCacheOptions(),
    retry: false, // Don't retry on GraphQL schema errors
  });

  useReactQueryErrorReporter(
    "Could not load the list of Career Plan Next Steps",
    error,
    false,
  );

  return {
    careerPlanNextSteps: data?.cmsCareerPlanNextSteps || [],
    loading: isLoading,
    error,
  };
};
